import type { TranslateService } from '@ngx-translate/core';
import type {
  OnyxColor,
  OnyxIconName,
  OnyxNamePipe,
  OnyxOptional,
  OnyxTooltipContext,
} from '@onyx/angular';
import { MapHelper, OnyxMapVehicleStatus } from '@onyx/angular';
import { chain } from 'lodash';
import type { TachographDriver } from '../../../../common/components/drivers-tachograph/drivers-tachograph.component';
import type { RoutePointValue } from '../../../../common/helpers/route.helper';
import { RouteHelper } from '../../../../common/helpers/route.helper';
import type { Point } from '../../../../common/interfaces/common/point';
import type { TelematicsItem } from '../../../../common/interfaces/telematics/telematics-item';
import type { TelematicsRoutePoint } from '../../../../common/interfaces/telematics/telematics-route-point';
import { TelematicsRoutePointStatus } from '../../../../common/interfaces/telematics/telematics-vehicle-point-status';
import type { RoutePoint } from '../../../../common/services/route.service';
import type { TelematicsStore } from '../../../../common/stores/telematics.store';
import type { SimplifiedDelegation } from '../../../delegations/common/interfaces/delegation';
import { DriverHelper } from '../../../drivers/common/helpers/driver.helper';
import { FleetHelper } from '../../../fleet/common/helpers/fleet.helper';
import { FleetTypeHelper } from '../../../fleet/common/helpers/fleet-type.helper';
import type { Fleet } from '../../../fleet/common/interfaces/fleet';

export class TelematicsHelper {
  public static getDriverIcon(
    data: OnyxOptional<TachographDriver | TelematicsItem>,
  ): OnyxIconName {
    const driver = data
      ? 'drivers' in data
        ? data.drivers.at(0)
        : data
      : null;
    if (!driver) return 'no-user-outlined';
    if (!driver.telematics) return 'no-integration-outlined';

    const workingMode = driver.tachograph?.workingMode;
    if (!workingMode) return 'tacho-connection-waiting-outlined';

    return `tacho-${workingMode}-outlined`;
  }

  public static getDriverTooltip(
    data: OnyxOptional<TachographDriver | TelematicsItem>,
    namePipe: OnyxNamePipe,
  ): OnyxTooltipContext | null {
    const driver = data
      ? 'drivers' in data
        ? data.drivers.at(0)
        : data
      : null;
    if (!driver) return null;

    return DriverHelper.getSelectedDriverTooltip(driver, namePipe);
  }

  public static getTelematicsFleetStatusIcon(
    status: OnyxMapVehicleStatus,
  ): OnyxIconName {
    switch (status) {
      case OnyxMapVehicleStatus.FREE_UNDER_24H:
      case OnyxMapVehicleStatus.FREE_OVER_24H:
        return 'vehicle-free-dotted-outlined';

      case OnyxMapVehicleStatus.FREE_DELEGATION_UNDER_24H:
      case OnyxMapVehicleStatus.FREE_DELEGATION_OVER_24H:
        return 'vehicle-free-outlined';

      case OnyxMapVehicleStatus.RIDE_ON_TIME:
      case OnyxMapVehicleStatus.RIDE_DELAY_RISK:
      case OnyxMapVehicleStatus.RIDE_DELAYED:
        return 'vehicle-ride-filled';

      case OnyxMapVehicleStatus.FAILURE:
        return 'alert-circle-two-tone-filled';
      case OnyxMapVehicleStatus.FAILURE_FREE:
        return 'alert-circle-outlined';
      case OnyxMapVehicleStatus.SEMI_TRAILER_FREE:
        return 'vehicle-semi-trailer-free-filled';
      case OnyxMapVehicleStatus.TRAILER_FREE:
        return 'vehicle-trailer-free-filled';

      case OnyxMapVehicleStatus.SEMI_TRAILER_LOADED:
      case OnyxMapVehicleStatus.TRAILER_LOADED:
      case OnyxMapVehicleStatus.SEMI_TRAILER_LOADED_WAITING:
      case OnyxMapVehicleStatus.TRAILER_LOADED_WAITING:
        return 'vehicle-disconnected-circle-filled';
    }
  }

  public static getTelematicsFleetStatusIconColor(
    status: OnyxMapVehicleStatus,
  ): OnyxColor | null {
    switch (status) {
      case OnyxMapVehicleStatus.FREE_UNDER_24H:
      case OnyxMapVehicleStatus.FREE_DELEGATION_UNDER_24H:
        return 'blue';

      case OnyxMapVehicleStatus.FREE_OVER_24H:
      case OnyxMapVehicleStatus.FREE_DELEGATION_OVER_24H:
        return 'red';

      case OnyxMapVehicleStatus.RIDE_ON_TIME:
        return 'green';
      case OnyxMapVehicleStatus.RIDE_DELAY_RISK:
        return 'orange';
      case OnyxMapVehicleStatus.RIDE_DELAYED:
        return 'red';

      case OnyxMapVehicleStatus.FAILURE_FREE:
        return 'yellow-alt';

      case OnyxMapVehicleStatus.TRAILER_FREE:
      case OnyxMapVehicleStatus.SEMI_TRAILER_FREE:
        return 'blue';

      case OnyxMapVehicleStatus.SEMI_TRAILER_LOADED:
      case OnyxMapVehicleStatus.TRAILER_LOADED:
        return 'red';

      case OnyxMapVehicleStatus.SEMI_TRAILER_LOADED_WAITING:
      case OnyxMapVehicleStatus.TRAILER_LOADED_WAITING:
        return 'blue-alt-dark';

      default:
        return null;
    }
  }

  public static getTelematicsFleetStatusTooltip(
    status: OnyxMapVehicleStatus,
    translateService: TranslateService,
  ): OnyxTooltipContext | undefined {
    const I18N = 'map.legend.vehicles';
    const translate = (key: string) => translateService.instant(key);

    const delegation = (
      delegation: boolean,
      duration: 'freeBelow24h' | 'freeOver24h',
    ) =>
      `${translate(`${I18N}.${delegation ? 'inDelegation' : 'withoutDelegation'}`)} · ${translate(`${I18N}.${duration}`)}`;

    const driving = (status: 'onTime' | 'delayRisk' | 'delayed') =>
      `${translate('labels.driving')} · ${translate(`${I18N}.${status}`)}`;

    switch (status) {
      case OnyxMapVehicleStatus.FREE_UNDER_24H:
        return delegation(false, 'freeBelow24h');
      case OnyxMapVehicleStatus.FREE_DELEGATION_UNDER_24H:
        return delegation(true, 'freeBelow24h');
      case OnyxMapVehicleStatus.FREE_OVER_24H:
        return delegation(false, 'freeOver24h');
      case OnyxMapVehicleStatus.FREE_DELEGATION_OVER_24H:
        return delegation(true, 'freeOver24h');

      case OnyxMapVehicleStatus.RIDE_ON_TIME:
        return driving('onTime');
      case OnyxMapVehicleStatus.RIDE_DELAY_RISK:
        return driving('delayRisk');
      case OnyxMapVehicleStatus.RIDE_DELAYED:
        return driving('delayed');

      case OnyxMapVehicleStatus.FAILURE_FREE:
        return translate(`${I18N}.failure`);
      case OnyxMapVehicleStatus.SEMI_TRAILER_FREE:
        return translate('labels.semiTrailer');
      case OnyxMapVehicleStatus.TRAILER_FREE:
        return translate('labels.trailer');
      case OnyxMapVehicleStatus.SEMI_TRAILER_LOADED:
      case OnyxMapVehicleStatus.SEMI_TRAILER_LOADED_WAITING:
        return translate('labels.disconnectedLoadedSemiTrailer');
      case OnyxMapVehicleStatus.TRAILER_LOADED:
      case OnyxMapVehicleStatus.TRAILER_LOADED_WAITING:
        return translate('labels.disconnectedLoadedTrailer');

      default:
        return undefined;
    }
  }

  public static getPoints(points: TelematicsRoutePoint[]): Point[] {
    return points.map(RouteHelper.getPointValue);
  }

  public static getPointIcon(
    point: RoutePoint | RoutePointValue,
  ): OnyxIconName {
    return MapHelper.getPointIconByType(RouteHelper.getType(point));
  }

  public static hasSwap(
    item: TelematicsItem | Fleet,
    telematicsStore: TelematicsStore,
  ): boolean {
    if (FleetHelper.isFleetType(item)) return !!item.relatedFleetSwap;

    return item.routePoints.some((point) => {
      const swapData = RouteHelper.getSwapData(point);
      if (!swapData) return false;

      const swapPoints = telematicsStore
        .swapPointsBySwapUuid()
        .get(swapData.uuid);
      if (!swapPoints?.length) return false;

      return swapPoints.some((point) => !RouteHelper.isCompleted(point));
    });
  }

  public static getDelegation(
    item: TelematicsItem,
  ): SimplifiedDelegation | null {
    return (
      item.drivers.find(({ delegation }) => !!delegation)?.delegation ?? null
    );
  }

  public static isTrailerFree(item: TelematicsItem): boolean {
    if (!FleetTypeHelper.isTrailer(item.generalInformation.category)) {
      return false;
    }

    return item.status.value.includes('free');
  }

  public static getProposalOrderIdentifiers(
    vehicle: TelematicsItem | null,
  ): string[] {
    if (!vehicle) return [];

    return chain(vehicle.routePoints)
      .filter((point) => RouteHelper.isProposition(point))
      .map((point) => point.order?.identifier)
      .compact()
      .uniq()
      .value();
  }

  public static getNextLoadedSemiTrailerPickupPoint(
    points: TelematicsRoutePoint[],
    trailerUuid: string,
  ): TelematicsRoutePoint | null {
    return (
      points.find(({ order, point, status }) => {
        if (status === TelematicsRoutePointStatus.COMPLETED) return false;
        if (!order || !point) return false;

        const { loadedSemiTrailer } = order.basicInformation;
        return (
          loadedSemiTrailer?.uuid === trailerUuid &&
          loadedSemiTrailer?.pickupPointUuid === point.uuid
        );
      }) ?? null
    );
  }
}
